import type { Color, SearchProduct, VariantSpec } from "../../openapi/shop_agent/client/types.gen";

// Use the Product type from shop_agent API
export type Product = SearchProduct;

// Re-export Color from the OpenAPI types
export type { Color };

type ProductIdFields = {
  id?: string | number;
  uuid?: string;
};

/**
 * Get a stable identifier for a product, preferring uuid over id.
 *
 * IMPORTANT: The backend collections/carts APIs require valid UUIDs. Products
 * should always have a `uuid` field populated. The `id` fallback exists for
 * legacy compatibility but may cause issues:
 *
 * 1. If a product was saved with an `id` that isn't a valid UUID, the backend
 *    will reject it with a 400 error.
 * 2. If a product was saved with an empty string "" (due to the old ?? operator
 *    treating "" as valid), those saved items won't load because "" isn't a
 *    valid UUID - they'll silently fail in getCatalogProducts().
 * 3. Products saved BEFORE this fix with corrupted IDs will remain broken in
 *    users' collections until manually removed.
 *
 * We use || instead of ?? so empty strings ("") fall back to the next value.
 * The old code used ?? which treated "" as valid, breaking collections.
 */
export function getProductAnyId(product: Product | null): string {
  if (!product) return "unknown";
  const p = product as Partial<ProductIdFields>;
  const uuid = typeof p.uuid === "string" ? p.uuid : undefined;
  const id = typeof p.id === "string" || typeof p.id === "number" ? String(p.id) : undefined;
  return uuid || id || "unknown";
}

/**
 * Get the product ID that matches what's in the user's saved collections.
 *
 * This checks both uuid and id against the isSaved function to handle cases
 * where products may have been saved under different identifiers. This is
 * important for the save/unsave UI to correctly reflect the saved state.
 *
 * See getProductAnyId() for important notes about UUID requirements.
 */
export function getSavedProductId(
  product: Product | null,
  isSaved: (id: string) => boolean
): string {
  if (!product) return "unknown";
  const p = product as Partial<ProductIdFields>;
  const uuid = typeof p.uuid === "string" ? p.uuid : undefined;
  const id = typeof p.id === "string" || typeof p.id === "number" ? String(p.id) : undefined;
  // Check if either identifier is already saved (for correct UI state)
  if (uuid && isSaved(uuid)) return uuid;
  if (id && isSaved(id)) return id;
  // For new saves, prefer uuid (required by backend)
  return uuid || id || "unknown";
}

// Domains that should not use wildfire URL wrapping due to compatibility issues
const AFFILIATE_BLACKLIST = [
  "www.hm.com",
  "www2.hm.com",
  // Add other problematic domains here as needed
];

const PLACEHOLDER_IMAGE = "/placeholder.jpg";

interface ImageObj {
  url?: string;
  gs_url?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface ProductImageSource {
  primary: string;
  fallback?: string;
  width?: number;
  height?: number;
}

const normalizeUrl = (value: string | null | undefined): string | null => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("/")) {
    return trimmed;
  }

  try {
    // Throws if not a valid absolute URL
    new URL(trimmed);
    return trimmed;
  } catch {
    return null;
  }
};

const createImageSource = (
  gsUrl: string | null,
  originalUrl: string | null
): ProductImageSource | null => {
  const cdnUrl = gsUrl ? normalizeUrl(gsUrlToHttp(gsUrl)) : null;
  const fallbackUrl = originalUrl ? normalizeUrl(originalUrl) : null;

  if (!cdnUrl && !fallbackUrl) {
    return null;
  }

  const primary = cdnUrl ?? fallbackUrl;
  if (!primary) return null;

  const fallback = cdnUrl && fallbackUrl && cdnUrl !== fallbackUrl ? fallbackUrl : undefined;

  return { primary, fallback };
};

const normalizeImageSource = (
  image: ImageObj | string | null | undefined
): ProductImageSource | null => {
  if (!image) return null;

  if (typeof image === "string") {
    const trimmed = image.trim();
    if (!trimmed) return null;

    if (trimmed.startsWith("gs://")) {
      return createImageSource(trimmed, null);
    }

    const url = normalizeUrl(trimmed);
    return url ? { primary: url } : null;
  }

  const gsUrl =
    typeof image.gs_url === "string" && image.gs_url.trim() ? image.gs_url.trim() : null;
  const httpUrl = typeof image.url === "string" && image.url.trim() ? image.url.trim() : null;

  const source = createImageSource(gsUrl, httpUrl);
  if (source) {
    const w = typeof image.width === "number" ? image.width : undefined;
    const h = typeof image.height === "number" ? image.height : undefined;
    if (w && h) return { ...source, width: w, height: h };
    return source;
  }

  // Fall back to any other string-like properties (e.g., image_url)
  for (const value of Object.values(image)) {
    if (typeof value === "string") {
      const normalized = normalizeUrl(value);
      if (normalized) {
        return { primary: normalized };
      }
    }
  }

  return null;
};

const dedupeImageSources = (sources: ProductImageSource[]): ProductImageSource[] => {
  const seen = new Map<string, ProductImageSource>();

  for (const source of sources) {
    const existing = seen.get(source.primary);
    if (!existing) {
      seen.set(source.primary, source);
      continue;
    }

    if ((!existing.fallback && source.fallback) || (!existing.width && source.width)) {
      seen.set(source.primary, {
        ...existing,
        fallback: existing.fallback ?? source.fallback,
        width: existing.width ?? source.width,
        height: existing.height ?? source.height,
      });
    }
  }

  return Array.from(seen.values());
};

/**
 * Get a product's name with fallback handling
 */
export function getProductName(product: Product | null): string {
  if (!product) return "Unnamed Product";
  return product.name || "Unnamed Product";
}

type ProductBrandFields = {
  brand_name?: string;
  catalog?: string;
  brand?: string | { name?: string };
  enrichment?: {
    canonical_brand?: {
      brand_name?: string;
    };
  };
};

export function getProductBrandLabel(product: Product | null): string {
  if (!product) return "";
  const p = product as Partial<ProductBrandFields>;
  const canonical = p.enrichment?.canonical_brand?.brand_name;
  if (typeof canonical === "string" && canonical.trim()) return canonical.trim();
  if (typeof p.brand_name === "string" && p.brand_name.trim()) return p.brand_name.trim();
  if (typeof p.brand === "string" && p.brand.trim()) return p.brand.trim();
  if (
    p.brand &&
    typeof p.brand === "object" &&
    typeof p.brand.name === "string" &&
    p.brand.name.trim()
  ) {
    return p.brand.name.trim();
  }
  if (typeof p.catalog === "string" && p.catalog.trim()) return p.catalog.trim();
  return "";
}

export function getProductCatalogDisplayName(product: Product | null): string {
  if (!product) return "";

  if (
    typeof product.catalog_display_name === "string" &&
    product.catalog_display_name.trim().length > 0
  ) {
    return product.catalog_display_name.trim();
  }

  return "";
}

export interface ProductAvailabilityStatus {
  availability: string | null;
  isOutOfStock: boolean;
  statusLabel: "This product may no longer be available" | "No Longer Available" | null;
}

export interface ProductPrimaryActionLabels {
  visibleLabel: string;
  accessibleLabel: string;
}

const normalizeAvailability = (value: unknown): string | null => {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const token = trimmed.split(/[/#]/).filter(Boolean).pop();
  if (!token) return null;

  return token.toLowerCase();
};

const getOfferAvailability = (offers: unknown): string | null => {
  if (!offers || typeof offers !== "object") return null;

  const offerRecord = offers as Record<string, unknown>;
  if (typeof offerRecord.availability === "string" && offerRecord.availability.trim()) {
    return offerRecord.availability.trim();
  }

  if (!Array.isArray(offerRecord.offers)) {
    return null;
  }

  // Upstream typically normalizes to a single effective offer state; if an offers[]
  // array is present, we take the first usable availability token to keep the UI deterministic.
  for (const nestedOffer of offerRecord.offers) {
    if (!nestedOffer || typeof nestedOffer !== "object") continue;

    const nestedAvailability = (nestedOffer as Record<string, unknown>).availability;
    if (typeof nestedAvailability === "string" && nestedAvailability.trim()) {
      return nestedAvailability.trim();
    }
  }

  return null;
};

export function getProductAvailabilityStatus(product: Product | null): ProductAvailabilityStatus {
  const availability = getOfferAvailability(product?.offers);
  const normalizedAvailability = normalizeAvailability(availability);

  if (normalizedAvailability === "outofstock") {
    return {
      availability,
      isOutOfStock: true,
      statusLabel: "This product may no longer be available",
    };
  }

  if (normalizedAvailability === "discontinued" || normalizedAvailability === "soldout") {
    return {
      availability,
      isOutOfStock: true,
      statusLabel: "No Longer Available",
    };
  }

  return {
    availability,
    isOutOfStock: false,
    statusLabel: null,
  };
}

export function getProductPrimaryActionLabels({
  catalogDisplayName,
  sellerDisplayName,
  isOutOfStock,
}: {
  catalogDisplayName?: string | null;
  sellerDisplayName?: string | null;
  isOutOfStock: boolean;
}): ProductPrimaryActionLabels {
  const normalizedCatalogDisplayName = catalogDisplayName?.trim() ?? "";
  const normalizedSellerDisplayName = sellerDisplayName?.trim() ?? "";

  if (isOutOfStock) {
    const sellerLabel = normalizedSellerDisplayName || normalizedCatalogDisplayName;
    if (!sellerLabel) {
      return {
        visibleLabel: "View item",
        accessibleLabel: "View item",
      };
    }

    const ctaLabel = `View on ${sellerLabel}`;
    return {
      visibleLabel: ctaLabel,
      accessibleLabel: ctaLabel,
    };
  }

  return {
    visibleLabel: "Buy now",
    accessibleLabel: normalizedCatalogDisplayName
      ? `Buy now from ${normalizedCatalogDisplayName}`
      : "Buy now",
  };
}

/**
 * Convert a Google Cloud Storage gs:// URL to an https URL that can be served by the CDN / GCS.
 * If the string is not a gs:// URL, it will be returned unchanged.
 */
export function gsUrlToHttp(gsUrl: string): string {
  if (!gsUrl || typeof gsUrl !== "string") return gsUrl;
  // Remove whitespace and validate prefix
  gsUrl = gsUrl.trim();
  if (!gsUrl.startsWith("gs://")) return gsUrl;

  // Strip the gs:// prefix and any leading slashes
  // Remove the gs:// prefix, any leading slashes, and any leading "/octogen-product-images" directory
  const path = gsUrl
    .replace(/^gs:\/\//, "")
    .replace(/^\/+/, "")
    .replace(/^octogen-product-images\/?/, ""); // replace the octogen-product-images directory so that it works with the CDN.
  // Use storage.googleapis.com as a default public base
  return `https://cdn.storerouter.ai/${path}`;
}

export function getProductImageSources(product: Product | null): ProductImageSource[] {
  if (!product) return [];

  try {
    const sources: ProductImageSource[] = [];

    if (product.image) {
      const primary = normalizeImageSource(product.image as ImageObj | string);
      if (primary) {
        sources.push(primary);
      }
    }

    if (product.images && Array.isArray(product.images)) {
      for (const image of product.images) {
        const normalized = normalizeImageSource(image as ImageObj | string);
        if (normalized) {
          sources.push(normalized);
        }
      }
    }

    // Legacy support for stray properties like imageUrl
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const legacyImageUrl = (product as any).imageUrl as string | undefined;
    if (legacyImageUrl) {
      const normalized = normalizeUrl(legacyImageUrl);
      if (normalized) {
        sources.push({ primary: normalized });
      }
    }

    const uniqueSources = dedupeImageSources(sources);

    if (uniqueSources.length === 0) {
      return [];
    }

    return uniqueSources;
  } catch (error) {
    console.error("Error constructing product image sources:", error);
    return [];
  }
}

/**
 * Get a product's image URL with fallback handling.
 */
export function getProductImage(product: Product | null): string {
  if (!product) return PLACEHOLDER_IMAGE;

  try {
    const sources = getProductImageSources(product);
    if (sources.length > 0 && sources[0]?.primary) {
      return sources[0].primary;
    }
  } catch (error) {
    console.error("Error getting product image:", error);
  }

  return PLACEHOLDER_IMAGE;
}

/**
 * Get all available primary image URLs for a product.
 */
export function getProductImages(product: Product | null): string[] {
  if (!product) return [];

  const sources = getProductImageSources(product);
  if (sources.length === 0) {
    return [];
  }

  return sources.map((source) => source.primary);
}

/**
 * Format a price with currency formatting
 */
export function formatPrice(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

export const parsePriceNumber = (val: unknown): number | null => {
  if (typeof val === "number" && !isNaN(val)) return val;
  if (typeof val === "string") {
    const cleaned = val.replace(/[^0-9.]+/g, "");
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

const normalizePriceType = (value: unknown): "sale" | "regular" | null => {
  if (typeof value !== "string") return null;
  const normalized = value.toLowerCase();
  if (normalized.includes("saleprice")) return "sale";
  // ListPrice (schema.org) is the original/regular price before discounts
  if (normalized.includes("regularprice") || normalized.includes("listprice")) return "regular";
  return null;
};

const getPriceComponentMatch = (
  components: Record<string, unknown>[]
): {
  sale?: { price: number };
  regular?: { price: number };
  first?: { price: number };
} => {
  const result: {
    sale?: { price: number };
    regular?: { price: number };
    first?: { price: number };
  } = {};

  for (const comp of components) {
    const price = parsePriceNumber(comp.price);
    if (price === null) continue;
    const priceType = normalizePriceType(comp.priceType);

    if (!result.first) {
      result.first = { price };
    }

    if (priceType === "sale" && !result.sale) {
      result.sale = { price };
    }

    if (priceType === "regular" && !result.regular) {
      result.regular = { price };
    }
  }

  return result;
};

/**
 * Get formatted price information with original price comparison
 */
export function getFormattedPrice(product: Product | null) {
  if (!product) return { final: "Price unavailable" };

  let finalPrice = parsePriceNumber(product.current_price);
  let originalPrice = parsePriceNumber(product.original_price);

  if (finalPrice === null && product.offers && typeof product.offers === "object") {
    const offers = product.offers as Record<string, unknown>;

    if (offers.priceSpecification && typeof offers.priceSpecification === "object") {
      const ps = offers.priceSpecification as Record<string, unknown>;
      if (ps.price !== undefined) {
        finalPrice = parsePriceNumber(ps.price);
      }

      if (finalPrice === null && Array.isArray(ps.priceComponent) && ps.priceComponent.length > 0) {
        const components = ps.priceComponent as Record<string, unknown>[];
        const { sale, regular, first } = getPriceComponentMatch(components);
        if (sale) {
          finalPrice = sale.price;
        }
        if (regular) {
          originalPrice = regular.price;
        }
        if (finalPrice === null && first) {
          finalPrice = first.price;
        }
      }
    } else if (typeof offers.lowPrice === "number" || typeof offers.lowPrice === "string") {
      finalPrice = parsePriceNumber(offers.lowPrice);
      const highParsed = parsePriceNumber(offers.highPrice);
      if (highParsed !== null && finalPrice !== null && highParsed > finalPrice) {
        originalPrice = highParsed;
      }
    } else if (Array.isArray(offers.offers) && offers.offers.length > 0) {
      const first = offers.offers[0] as Record<string, unknown>;
      if (first?.priceSpecification) {
        const fps: Record<string, unknown> = first.priceSpecification as Record<string, unknown>;
        if (fps.price !== undefined) {
          finalPrice = parsePriceNumber(fps.price);
        } else if (Array.isArray(fps.priceComponent) && fps.priceComponent.length > 0) {
          const components = fps.priceComponent as Record<string, unknown>[];
          const { sale, regular, first: fallback } = getPriceComponentMatch(components);
          if (sale) {
            finalPrice = sale.price;
          }
          if (regular) {
            originalPrice = regular.price;
          }
          if (finalPrice === null && fallback) {
            finalPrice = fallback.price;
          }
        }
      }
    }
  }

  if (finalPrice === null) {
    return { final: "Price unavailable" };
  }

  // If both prices exist and original is higher than final, show both
  if (originalPrice !== null && originalPrice > finalPrice) {
    return {
      final: formatPrice(finalPrice),
      original: formatPrice(originalPrice),
    };
  }

  // Otherwise just show final price
  return {
    final: formatPrice(finalPrice),
  };
}

/**
 * Calculate and format a product's rating information
 */
export function getRatingForDisplay(product: Product | null) {
  if (!product) return 0;

  // Check if product has a direct rating object
  if (product.rating) {
    return {
      average_rating: product.rating.average_rating || 0,
      rating_count: product.rating.rating_count || 0,
    };
  }

  // Check if review array exists and has reviews with ratings
  if (product.review && product.review.length > 0) {
    // Count reviews with valid ratings
    const reviewsWithRatings = product.review.filter(
      (r) =>
        r.reviewRating &&
        r.reviewRating.average_rating !== undefined &&
        r.reviewRating.average_rating !== null &&
        typeof r.reviewRating.average_rating === "number"
    );

    if (reviewsWithRatings.length > 0) {
      // Calculate average rating from reviews
      const sum = reviewsWithRatings.reduce(
        (acc, r) => acc + (r.reviewRating?.average_rating || 0),
        0
      );
      return {
        average_rating: sum / reviewsWithRatings.length,
        rating_count: product.review.length,
      };
    }
  }

  // Default fallback
  return 0;
}

/**
 * Create wildfire wrapped URL for tracking
 */
export function getWildfireUrl(productUrl: string | null | undefined): string | null {
  if (!productUrl) return null;

  try {
    const url = new URL(productUrl);
    const targetDomain = url.hostname;
    const deepLinkUrl = productUrl;

    // Check if domain is blacklisted from affiliate tracking
    if (AFFILIATE_BLACKLIST.includes(targetDomain)) {
      console.warn(
        `Domain ${targetDomain} is blacklisted from affiliate tracking - returning original URL`
      );
      return productUrl;
    }

    // Construct the wildfire URL with proper encoding
    const wildfireUrl = `https://wild.link/e?d=32881444&dc=${encodeURIComponent(targetDomain)}&url=${encodeURIComponent(deepLinkUrl)}`;
    return wildfireUrl;
  } catch {
    return productUrl; // Fallback to original URL if parsing fails
  }
}

/**
 * Formats an array feature for display
 */
export function formatArrayFeature(
  value: unknown,
  maxItems: number = 3
): { items: string[]; remaining: number; toString: () => string } | string | null {
  if (!value) {
    return null;
  }

  if (!Array.isArray(value)) {
    return String(value);
  }

  if (value.length === 0) {
    return null;
  }

  const getItemString = (item: unknown): string => {
    if (item === null || item === undefined) {
      return "";
    }

    if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
      return String(item);
    }

    if (
      typeof item === "object" &&
      "label" in item &&
      typeof (item as { label: unknown }).label === "string"
    ) {
      return (item as { label: string }).label;
    }

    return String(item);
  };

  const items = (value as unknown[]).slice(0, maxItems).map(getItemString).filter(Boolean);
  const remaining =
    (value as unknown[]).length > maxItems ? (value as unknown[]).length - maxItems : 0;

  return {
    items,
    remaining,
    toString: () => {
      const result = items.join(", ");
      return remaining > 0 ? `${result} +${remaining} more` : result;
    },
  };
}

// -------------------- Variant helpers --------------------

export function productHasColorVariants(product: Product | null): boolean {
  return !!(product?.color_info?.colors?.length && product.color_info.colors.length > 1);
}

export function productHasSizeVariants(product: Product | null): boolean {
  return !!(product?.sizes?.length && product.sizes.length > 1);
}

export function productHasPatternVariants(product: Product | null): boolean {
  return !!(product?.patterns?.length && product.patterns.length > 1);
}

export function productHasFitVariants(product: Product | null): boolean {
  // Check if fit is an array with multiple options
  if (Array.isArray(product?.fit)) {
    return product.fit.length > 1;
  }
  return false;
}

/**
 * Determine if all required variant selections have been made for the product
 */
export function areVariantsSelected(
  product: Product | null,
  selections: {
    color?: Color | null;
    size?: string | null;
    pattern?: string | null;
    fit?: string | null;
  },
  options?: {
    allowMissingSize?: boolean;
    allowMissingColor?: boolean;
  }
): boolean {
  if (!product) return false;
  const needsColor = productHasColorVariants(product);
  const needsSize = productHasSizeVariants(product);
  const needsPattern = productHasPatternVariants(product);
  const needsFit = productHasFitVariants(product);
  const allowMissingSize = options?.allowMissingSize ?? false;
  const allowMissingColor = options?.allowMissingColor ?? false;

  if (needsColor && !allowMissingColor && !selections.color) return false;
  if (needsSize && !allowMissingSize && !selections.size) return false;
  if (needsPattern && !selections.pattern) return false;
  if (needsFit && !selections.fit) return false;

  return true;
}

export function getDefaultSelectedColor(product: Product | null): Color | null {
  if (!product) return null;
  if (productHasColorVariants(product)) return null;
  return (product.color_info?.colors?.[0] || null) as Color | null;
}

export function getDefaultSelectedSize(product: Product | null): string | null {
  if (!product) return null;
  if (productHasSizeVariants(product)) return null;
  return product.sizes?.[0] || null;
}

export function getDefaultSelectedPattern(product: Product | null): string | null {
  if (!product) return null;
  if (productHasPatternVariants(product)) return null;
  return product.patterns?.[0] || null;
}

export function getDefaultSelectedFit(product: Product | null): string | null {
  if (!product) return null;
  if (productHasFitVariants(product)) return null;
  return Array.isArray(product.fit) ? product.fit[0] : null;
}

/**
 * Build a VariantSpec from selected variants
 */
export function buildVariantSpec(selections: {
  color?: Color | null;
  size?: string | null;
  pattern?: string | null;
  fit?: string | null;
}): VariantSpec {
  return {
    color: selections.color?.label || undefined,
    size: selections.size || undefined,
    pattern: selections.pattern || undefined,
    fit: selections.fit || undefined,
  };
}
